import axios, {
  HttpStatusCode,
  type AxiosError,
  type AxiosResponse,
} from "axios";

import {
  apiBaseUrl,
  ApiException,
  clearStorageTokens,
  createApiErrorWithTranslation,
  EMPTY_RESPONSE_ERROR,
  errors,
  handleForbiddenResponseException,
  Log,
  logApiError,
  normalizeApiError,
  normalizeUrlBasePath,
  ResponseStatusEnum,
  type IApiError,
  type TApiSuccess,
} from "@ryde/shared";

export const getLandingApiUrl = (): string | undefined => {
  return normalizeUrlBasePath(apiBaseUrl() || "");
};

export const handleApiLandingException = async <T>({
  e,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  e: any;
}): Promise<AxiosResponse<TApiSuccess<T> | IApiError> | undefined> => {
  if (axios.isCancel(e)) {
    throw e instanceof Error ? e : new Error(String(e));
  }

  const failedResponse = (e as AxiosError<IApiError, unknown>).response;

  if (!failedResponse?.status) {
    throw new ApiException(EMPTY_RESPONSE_ERROR, HttpStatusCode.BadRequest);
  }

  logApiError(failedResponse?.status, e);

  switch (failedResponse.status) {
    case HttpStatusCode.Unauthorized:
      try {
        return await handleUnauthorizedLandingResponseException<T>(e);
      } catch (err) {
        Log.warn("Error trying to auto-refresh token on api call", err);

        throw err;
      }

    case HttpStatusCode.Forbidden:
      return handleForbiddenResponseException(failedResponse);

    case HttpStatusCode.NotFound:
      throw new ApiException(
        normalizeApiError({
          apiError: failedResponse.data,
          fallbackTranslationKey: errors.common.notFoundResponseStatus,
        }),
        HttpStatusCode.NotFound,
      );

    default:
      if (failedResponse.data?.status === ResponseStatusEnum.Error) {
        throw new ApiException(failedResponse.data, e.response.status);
      }

      return undefined;
  }
};

export const handleUnauthorizedLandingResponseException = async <T>(
  e: AxiosError<IApiError, unknown>,
): Promise<Promise<AxiosResponse<TApiSuccess<T>, unknown>>> => {
  const message =
    (e.response?.data?.message as string) ||
    e.message ||
    "Unknown authentication error";

  clearStorageTokens();

  throw new ApiException(
    createApiErrorWithTranslation({
      message,
      translationKey: errors.common.authenticationError,
    }),
    HttpStatusCode.Unauthorized,
  );
};
